import { supabase } from '../supabaseClient';
import { dutyBoardService, toRosterRuleViolation, type RosterRuleViolation } from './dutyBoardService';
import type {
  ApplyResponse,
  BreakRequirement,
  CandidateGuard,
  CandidateQuery,
  GuardAvailabilityPreference,
  OptionPreviewRequest,
  PreviewResponse,
  RosterChangeOperation,
  RosterComplianceGateway,
  RosterRule,
  RosterSite,
  RosterSnapshot,
} from './rosterCompliance';

const numberOrNull = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

function toViolations(value: unknown, slotDate: string): RosterRuleViolation[] {
  if (!Array.isArray(value)) return [];
  return value.map((row) => {
    const record = row as Record<string, unknown>;
    // Freshly evaluated rows carry no date of their own; they belong to the roster previewed.
    return { ...toRosterRuleViolation(record), slot_date: (record.slot_date as string | undefined) ?? slotDate };
  });
}

function toBreakRequirements(value: unknown): BreakRequirement[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((tier) => {
    const record = tier as Record<string, unknown>;
    const workMinutesOver = numberOrNull(record.work_minutes_over);
    const minimumBreakMinutes = numberOrNull(record.minimum_break_minutes);
    return workMinutesOver !== null && minimumBreakMinutes !== null ? [{ workMinutesOver, minimumBreakMinutes }] : [];
  });
}

function toRosterRule(row: Record<string, unknown>): RosterRule {
  return {
    id: row.id as string,
    name: row.name as string,
    countryCode: ((row.country_code as string | null) ?? '').trim(),
    minimumRestMinutes: numberOrNull(row.minimum_rest_minutes),
    maxShiftMinutes: numberOrNull(row.max_shift_minutes),
    maxConsecutiveDays: numberOrNull(row.max_consecutive_days),
    maxAverageWeeklyMinutes: numberOrNull(row.max_average_weekly_minutes),
    weeklyReferenceWeeks: numberOrNull(row.weekly_reference_weeks),
    breakRequirements: toBreakRequirements(row.break_requirements),
    maxNightShiftMinutes: numberOrNull(row.max_night_shift_minutes),
    sourceUrls: Array.isArray(row.source_urls) ? (row.source_urls as unknown[]).filter((url): url is string => typeof url === 'string') : [],
  };
}

const AVAILABILITY: GuardAvailabilityPreference[] = ['available', 'preferred', 'unavailable'];

function toCandidateGuard(row: Record<string, unknown>): CandidateGuard {
  const availability = row.availability as GuardAvailabilityPreference | null;
  return {
    guardId: row.guard_id as string,
    fullName: (row.full_name as string | null) ?? 'Unknown guard',
    avatarUrl: (row.avatar_url as string | null) ?? null,
    hasRequiredLicence: Boolean(row.has_required_licence),
    onApprovedLeave: Boolean(row.on_approved_leave),
    availability: availability && AVAILABILITY.includes(availability) ? availability : null,
    overlappingShiftSummary: (row.overlapping_shift_summary as string | null) ?? null,
    restBeforeMinutes: numberOrNull(row.rest_before_minutes),
    restAfterMinutes: numberOrNull(row.rest_after_minutes),
    mergedShiftMinutes: numberOrNull(row.merged_shift_minutes) ?? 0,
    consecutiveWorkDays: numberOrNull(row.consecutive_work_days) ?? 1,
    referenceMinutes: numberOrNull(row.reference_minutes) ?? 0,
    weekMinutes: numberOrNull(row.week_minutes) ?? 0,
    siteShiftCount: numberOrNull(row.site_shift_count) ?? 0,
  };
}

/** Supabase side of roster fixing: the gateway the remediation engine runs against. */
export const rosterComplianceService: RosterComplianceGateway = {
  async loadSnapshot(site: RosterSite, slotDate: string): Promise<RosterSnapshot> {
    const [slots, breakSlots] = await Promise.all([
      dutyBoardService.fetchSlots(site.id, slotDate),
      dutyBoardService.fetchBreakSlots(site.id, slotDate),
    ]);
    return { site, slotDate, slots, breakSlots };
  },

  /** The active rule row for a country, chosen the same way the evaluators choose it. */
  async loadRule(countryCode: string): Promise<RosterRule | null> {
    const code = countryCode.trim().toUpperCase();
    if (!code) return null;
    const { data, error } = await supabase
      .from('roster_rules')
      .select(`
        id,
        name,
        country_code,
        minimum_rest_minutes,
        max_shift_minutes,
        max_consecutive_days,
        max_average_weekly_minutes,
        weekly_reference_weeks,
        break_requirements,
        max_night_shift_minutes,
        source_urls
      `)
      .eq('country_code', code)
      .eq('is_active', true)
      .order('updated_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? toRosterRule(data as Record<string, unknown>) : null;
  },

  async findCandidates(siteId: string, slotDate: string, query: CandidateQuery): Promise<CandidateGuard[]> {
    const { data, error } = await supabase.rpc('roster_fix_candidate_guards', {
      p_site_id: siteId,
      p_slot_date: slotDate,
      p_start_minute: query.startMinute,
      p_end_minute: query.endMinute,
      p_required_licence_type_id: query.requiredLicenceTypeId,
      p_exclude_guard_ids: query.excludeGuardIds,
    });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toCandidateGuard);
  },

  async previewOptions(siteId: string, slotDate: string, options: OptionPreviewRequest[]): Promise<PreviewResponse> {
    const { data, error } = await supabase.rpc('preview_roster_fix_options', {
      p_site_id: siteId,
      p_slot_date: slotDate,
      p_options: options,
    });
    if (error) throw error;
    const record = (data ?? {}) as Record<string, unknown>;
    const rawOptions = Array.isArray(record.options) ? record.options as Record<string, unknown>[] : [];
    return {
      baseline: toViolations(record.baseline, slotDate),
      options: rawOptions.map((option) => ({
        key: option.key as string,
        violations: Array.isArray(option.violations) ? toViolations(option.violations, slotDate) : null,
        error: (option.error as string | null) ?? null,
      })),
    };
  },

  async applyOperations(siteId: string, slotDate: string, operations: RosterChangeOperation[]): Promise<ApplyResponse> {
    const { data, error } = await supabase.rpc('apply_roster_fix_operations', {
      p_site_id: siteId,
      p_slot_date: slotDate,
      p_operations: operations,
    });
    if (error) throw error;
    const record = (data ?? {}) as Record<string, unknown>;
    return {
      appliedOperations: numberOrNull(record.applied_operations) ?? operations.length,
      violations: toViolations(record.violations, slotDate),
    };
  },
};
